import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import styled from "@emotion/styled";
import { FC, useState } from "react";
import { Field, useFormState } from "react-final-form";

import { DragIndicator, Error, TextFields } from "@livechat/design-system-icons";
import {
  Accordion,
  DesignToken,
  Heading,
  Icon,
  SpacingToken,
  Switch,
  Tag,
} from "@livechat/design-system-react-components";

import { FormBlockType, IFormBlock } from "store/entities/contact-forms/interfaces";
import { textEllipsisStyles } from "styles/common";
import { withVar } from "themes/helpers";

import { ConsentBlock } from "./blocks/ConsentBlock";
import { EmailBlock } from "./blocks/EmailBlock";
import { HeaderBlock } from "./blocks/HeaderBlock";
import { InputBlock } from "./blocks/InputBlock";
import { MessageBlock } from "./blocks/MessageBlock";
import { OptionsBlock } from "./blocks/OptionsBlock";
import { ParagraphBlock } from "./blocks/ParagraphBlock";
import { SelectionOptionsBlock } from "./blocks/SelectionOptionsBlock";
import { SubjectBlock } from "./blocks/SubjectBlock";
import { SubmitBlock } from "./blocks/SubmitBlock";
import { BLOCK_PROPERTIES, ICON_MAP, USER_INPUT_BLOCKTYPES } from "./constants";
import { getFieldLabel, getFormErrors } from "./helpers";

type Props = {
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  fieldLabel: string;
  fieldName: string;
  attributes?: {
    role?: string;
    roleDescription?: string;
    tabIndex?: number;
  };
  listeners?: SyntheticListenerMap;
  fieldType: FormBlockType;
  isDragging?: boolean;
};

export const FormBlock: FC<Props> = ({
  isOpen,
  onOpen,
  onClose,
  fieldLabel,
  fieldName,
  attributes,
  listeners,
  fieldType,
  isDragging,
}) => {
  const [hover, setHover] = useState(false);
  const { errors, values } = useFormState<{ fields: IFormBlock[] }>();
  const [isInteractingWithForm, setIsInteractingWithForm] = useState(false);

  const shouldShowDragIndicator = hover && !BLOCK_PROPERTIES[fieldType]?.static;

  const errorsMap = getFormErrors(errors, fieldName, fieldType);
  const valueBasedLabel = getFieldLabel(values, fieldName, fieldType, fieldLabel);

  const handleMouseEnter = (): void => {
    setIsInteractingWithForm(true);
  };

  const handleMouseLeave = (): void => {
    setIsInteractingWithForm(false);
  };

  return (
    <Accordion
      isOpen={isOpen}
      onOpen={onOpen}
      css={
        isDragging && {
          borderColor: withVar(DesignToken.BorderBasicHover),
          boxShadow: "0 2px 6px 0 #13131733",
        }
      }
      onClose={onClose}
      kind={errorsMap[fieldType]?.length ? "error" : undefined}
      // @ts-expect-error TS(2322)
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      {...(!isInteractingWithForm ? listeners : {})}
      {...attributes}
      data-testid={`${fieldName}-${fieldType}-block`}
      label={{
        open: (
          <LabelWrapper
            css={{ cursor: shouldShowDragIndicator ? "grab" : "pointer" }}
            data-testid={`${fieldName}-${fieldType}-block-open-label`}
          >
            <Icon source={shouldShowDragIndicator ? DragIndicator : (ICON_MAP[fieldType] ?? TextFields)} />
            <Heading size="xs">{fieldLabel}</Heading>
            {USER_INPUT_BLOCKTYPES.includes(fieldType) && (
              <span
                css={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  marginLeft: "auto",
                }}
              >
                <Field
                  name={`${fieldName}.required`}
                  type="checkbox"
                  render={({ input }) => (
                    <>
                      <Switch
                        {...input}
                        on={input.checked}
                        disabled={fieldType === "email"}
                        size="medium"
                        css={{ marginRight: withVar(SpacingToken.Spacing2) }}
                        onChange={() => {
                          input.onChange(!input.checked);
                        }}
                        data-testid="required-switch"
                        // DS doesn't have this prop but we need it for the accordion to prevent closing
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        onClick={e => {
                          e.stopPropagation();
                        }}
                      />
                      <span>Required</span>
                    </>
                  )}
                />
              </span>
            )}
          </LabelWrapper>
        ),
        closed: (
          <LabelWrapper
            css={{ cursor: shouldShowDragIndicator ? "grab" : "pointer" }}
            data-testid={`${fieldName}-${fieldType}-block-closed-label`}
          >
            <Icon
              source={shouldShowDragIndicator ? DragIndicator : (ICON_MAP[fieldType] ?? TextFields)}
              css={{ cursor: shouldShowDragIndicator ? "grab" : "pointer" }}
            />
            {errorsMap[fieldType]?.length ? (
              errorsMap[fieldType].map((error, index) => (
                <Tag key={index} kind="error" leftNode={<Icon source={Error} />}>
                  {error}
                </Tag>
              ))
            ) : (
              <Heading size="xs" css={textEllipsisStyles}>
                {valueBasedLabel}
              </Heading>
            )}
          </LabelWrapper>
        ),
      }}
    >
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {fieldType === "paragraph" ? (
          <ParagraphBlock name={fieldName} />
        ) : fieldType === "header" ? (
          <HeaderBlock name={fieldName} />
        ) : fieldType === "subject" ? (
          <SubjectBlock name={fieldName} blockType={fieldType} />
        ) : fieldType === "consent" ? (
          <ConsentBlock name={fieldName} blockType={fieldType} />
        ) : fieldType === "select" || fieldType === "radio" || fieldType === "checkbox" ? (
          <SelectionOptionsBlock name={fieldName} blockType={fieldType} />
        ) : fieldType === "email" ? (
          <EmailBlock name={fieldName} />
        ) : fieldType === "input" || fieldType === "name" ? (
          <InputBlock name={fieldName} blockType={fieldType} />
        ) : fieldType === "textarea" ? (
          <MessageBlock name={fieldName} />
        ) : fieldType === "submit" ? (
          <SubmitBlock name={fieldName} />
        ) : fieldType === "options" ? (
          <OptionsBlock name={fieldName} />
        ) : null}
      </div>
    </Accordion>
  );
};

const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  width: 412px;
  > *:not(:last-child) {
    margin-right: var(${SpacingToken.Spacing2});
  }
`;
